import { TextAlignment } from "building-types/annotation/annotationTypesEnum";
import { getSystemDataSequenceName } from "building-types/dataSequences/dataSequenceTools";
import { isSystemDataSequenceId } from "building-types/dataSequences/isSystemDataSequenceId";
import type {
  BuiltInDataTableSettings,
  KeyAttributeSettingOptions,
  KeyMeasureSettingOptions,
} from "building-types/dataSequences/scheduleSettingsTable";
import type { Doc } from "building-types/doc";
import { docEntityBaseNew } from "building-types/entities/docEntityBaseNew";
import { isParameterRef } from "building-types/options/isParameterRef";
import type { ParameterRef } from "building-types/options/parameter";
import { ZERO_INCHES, DEFAULT_LARGE_FONT_SIZE } from "building-types/plans/constants";
import type { EvaluatedDataTable } from "building-types/views/planSets";
import uid from "common/utils/uid";
import { BASE_PRESET_NAME } from "../doc/docDefaultConstants";
import { docEntityIdFromString } from "../doc/docEntityId";
import type { DataTableTitleConfig } from "./dataTable";
import { type DataTable, type DataTableColumnConfig } from "./dataTable";

export function dataTableNew({
  x = ZERO_INCHES,
  y = ZERO_INCHES,
  width = ZERO_INCHES,
  height = ZERO_INCHES,
  presetId = docEntityIdFromString("PRESET", BASE_PRESET_NAME),
  optionSetOverrides = {},
  dataSequenceId = "BUILDING_AREA",
  name = "Data Table",
  findAndReplace,
  levelId,
  parentId,
  id = uid(),
}: Partial<DataTable>): DataTable {
  return {
    ...docEntityBaseNew("DATA_TABLE"),
    id,
    name,
    x,
    y,
    width,
    height,
    presetId,
    optionSetOverrides,
    findAndReplace: findAndReplace ?? {},
    dataSequenceId,
    levelId,
    parentId,
    columnConfig: {},
  };
}

export type ColumnConfigEntryWithID = [
  keyof { id: string } & Partial<DataTableColumnConfig>,
  { id: string } & Partial<DataTableColumnConfig>,
];

/**
 * KAM data is stored in measures or attributes table with potentially the same key. This makes it impossible to
 * store the column config in a simple object. This function decodes the column to a unique key that includes
 * the type of the column.
 */
export function encodeColumnConfigId(id: string, x: { isAggregated?: boolean }): ColumnConfigEntryWithID[0] {
  return `${id}${x?.isAggregated ? "_AGGREGATED" : ""}` as ColumnConfigEntryWithID[0];
}

/**
 * KAM data is stored in measures or attributes table with potentially the same key. This makes it impossible to
 * store the column config in a simple object. This function decodes the column to a unique key that includes
 * the type of the column.
 */
export function decodeColumnConfigId(id: string): { id: string; isAggregated: boolean } {
  if (id.endsWith("_AGGREGATED")) {
    return { id: id.slice(0, -"_AGGREGATED".length), isAggregated: true };
  }
  return { id, isAggregated: false };
}

// TODO @paulscrugham: this is not right, we should be using an evaluated dataSequenceId
function dataSequenceIdFromParameterRef(
  parameterRef: ParameterRef,
  doc: Pick<Doc, "localParameters" | "globalParameters">,
): string | undefined {
  const parameter = doc.localParameters[parameterRef.id] ?? doc.globalParameters[parameterRef.id];

  if (!parameter || typeof parameter.defaultValue !== "string") {
    return undefined;
  }

  return parameter.defaultValue;
}

export function defaultDataTableTitle(
  dataSequenceId: DataTable["dataSequenceId"],
  doc: Pick<Doc, "localParameters" | "globalParameters" | "dataSequences">,
): string | undefined {
  const dataSequenceIdEvaluated = isParameterRef(dataSequenceId)
    ? dataSequenceIdFromParameterRef(dataSequenceId, doc)
    : dataSequenceId;

  if (!dataSequenceIdEvaluated) {
    return undefined;
  }

  if (isSystemDataSequenceId(dataSequenceIdEvaluated)) {
    return getSystemDataSequenceName(dataSequenceIdEvaluated);
  }

  return doc.dataSequences[dataSequenceIdEvaluated]?.name;
}

export const DEFAULT_DATA_TABLE_FONT_SIZE = DEFAULT_LARGE_FONT_SIZE;

export const DEFAULT_DATA_TABLE_TITLE_ALIGNMENT = TextAlignment.CENTER;

export function resolveDataTableTitleConfig(
  dataTable: EvaluatedDataTable | DataTable,
  doc: Pick<Doc, "localParameters" | "globalParameters" | "dataSequences">,
): Required<DataTableTitleConfig> {
  return {
    titleHidden: !!dataTable.titleHidden,
    title: dataTable.title ?? defaultDataTableTitle(dataTable.dataSequenceId, doc) ?? "",
    titleFontSize: dataTable.titleFontSize ?? DEFAULT_DATA_TABLE_FONT_SIZE,
    titleAlignment: dataTable.titleAlignment ?? DEFAULT_DATA_TABLE_TITLE_ALIGNMENT,
  };
}

export function getAttributesAndMeasuresListToShow(
  typeSettings: BuiltInDataTableSettings,
): [string, Partial<KeyAttributeSettingOptions & KeyMeasureSettingOptions>][] {
  const result: [string, KeyAttributeSettingOptions | KeyMeasureSettingOptions][] = [];
  const attributes = Object.entries(typeSettings.attributes).filter(([, attr]) => attr.definesEntity);
  const measures = Object.entries(typeSettings.measures).filter(([, attr]) => attr.isAggregated);
  result.push(...attributes);
  result.push(...measures);
  result.sort(([, a], [, b]) => a.index - b.index);
  return result;
}
